import axios from 'axios';
import {
  RuleModel,
  RulesEngineActionLog,
  RulesEngineRunLog,
} from '../../types';
import { auth } from '../shared/firebase/firebase-config';

const BASE_URL =
  import.meta.env.MODE === 'development'
    ? 'http://localhost:8888'
    : window.location.origin;

const instance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
});

instance.interceptors.request.use(async (config) => {
  const idToken = await auth.currentUser?.getIdToken();
  if (idToken) {
    config.headers.Authorization = `Bearer ${idToken}`;
  }
  return config;
});

export const RulesKeys = {
  RulesList: 'RulesList',
  RuleDetails: 'RuleDetails',
  RunLogs: 'RunLogs',
  RunLogsCounts: 'RunLogsCounts',
  RunLogActions: 'RunLogActions',
};

export async function listAllRules() {
  const res = await instance.get<RuleModel[]>(`/api/rulesEngine`);
  return res.data;
}

export async function addRule(
  model: Partial<RuleModel>,
  changeLogMessage: string
) {
  const res = await instance.post<RuleModel>(
    `/api/rulesEngine/addRule`,
    model,
    {
      params: {
        changeLogMessage,
      },
    }
  );
  return res.data;
}

export async function editRule(model: RuleModel, changeLogMessage: string) {
  const res = await instance.put<RuleModel>(
    `/api/rulesEngine/editRule`,
    model,
    {
      params: {
        changeLogMessage,
      },
    }
  );
  return res.data;
}

export async function enableRules(
  modelIds: number[],
  changeLogMessage: string
) {
  const res = await instance.put<void>(
    `/api/rulesEngine/enableRules`,
    modelIds,
    {
      params: {
        changeLogMessage,
      },
    }
  );
  return res.data;
}

export async function disableRules(
  modelIds: number[],
  changeLogMessage: string
) {
  const res = await instance.put<void>(
    `/api/rulesEngine/disableRules`,
    modelIds,
    {
      params: {
        changeLogMessage,
      },
    }
  );
  return res.data;
}

export async function deleteRules(
  modelIds: number[],
  changeLogMessage: string
) {
  const res = await instance.delete<void>(`/api/rulesEngine/deleteRules`, {
    data: modelIds,
    params: {
      changeLogMessage,
    },
  });
  return res.data;
}

export async function getRuleRunLogs(ruleId: number) {
  const res = await instance.get<RulesEngineRunLog[]>(
    `/api/rulesEngine/runlogs/${ruleId}`
  );

  return res.data;
}

export async function getRuleRunLogsCount(ruleId: number) {
  const res = await instance.get<{
    total: number;
    successful: number;
    failed: number;
    lastTriggered?: number;
  }>(`/api/rulesEngine/runlogs/count/${ruleId}`);

  return res.data;
}

export async function getRunLogActions(runLogId: number, createdDate: number) {
  const res = await instance.get<RulesEngineActionLog[]>(
    `/api/rulesEngine/actionlogs/${runLogId}`,
    {
      params: {
        createdDate,
      },
    }
  );
  return res.data;
}

export async function getAllRunLogActions(createdDate: number) {
  const res = await instance.get<RulesEngineActionLog[]>(
    `/api/rulesEngine/actionlogs`,
    {
      params: {
        createdDate,
      },
    }
  );
  return res.data;
}

export async function saveRule(model: RuleModel, changeLogMessage: string) {
  const res = await instance.post<RuleModel>(
    `/api/rulesEngine/saveRule`,
    model,
    {
      params: {
        changeLogMessage,
      },
    }
  );
  return res.data;
}

export interface PreviewImpactRequest {
  model: Partial<RuleModel>;
  startDate: number;
  endDate: number;
}

export interface PreviewImpactResult {
  matchCount: number;
  hasData: boolean;
}

export async function previewImpact(
  payload: PreviewImpactRequest,
  signal?: AbortSignal
): Promise<PreviewImpactResult> {
  const res = await instance.post<PreviewImpactResult>(
    `/api/rulesEngine/previewImpact`,
    payload,
    { signal }
  );
  return res.data;
}
